/**
 * Fabric retry helper for marketplace API calls.
 *
 * In service-identity mode, per-user Fabric CA re-enrollment is obsolete.
 * On FABRIC_ACCESS_DENIED, we ensure ledger onboarding via POST /api/registerUser
 * (idempotent) and retry the original request once.
 */

import { getAuth } from 'firebase/auth';
import config from './EnvironmentSwitcher';

/**
 * Ensure the current Firebase user exists on the ledger (RegisterUser chaincode).
 * @returns {Promise<void>}
 */
export async function ensureLedgerUser() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken(true);
  const response = await fetch(`${config.apiUrl}/api/registerUser`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const body = JSON.parse(text);
      message = body.message || body.error || text;
    } catch (_) {}
    throw new Error(message || `Ledger onboarding failed: ${response.status}`);
  }
}

/** @deprecated Use ensureLedgerUser — kept for callers that still import reEnroll. */
export async function reEnroll() {
  return ensureLedgerUser();
}

/**
 * Same as fetch(), but on 403 with body.code === 'FABRIC_ACCESS_DENIED' we call
 * ensureLedgerUser() once and retry the request.
 */
export async function fetchWithFabricRetry(url, options = {}) {
  let res = await fetch(url, options);
  if (res.status === 403) {
    let body = {};
    try {
      body = await res.clone().json();
    } catch (_) {}
    if (body.code === 'FABRIC_ACCESS_DENIED') {
      await ensureLedgerUser();
      res = await fetch(url, options);
    }
  }
  return res;
}
